import type { HistoryEventRecord } from '../../domain/types.js';
import type { Npc } from '../../types.js';
import { chatComplete, deepSeekReady } from '../../llm/client.js';
import { z } from 'zod';
import { initWorld, type WorldState } from '../useSim.js';
import { isRetiredHistorySummary } from './persistence.js';
import { recordHistoryEvent } from './adapter.js';
import type { PersistedRuntimeState } from './types.js';
import { uid } from '../spawn.js';

export function initAquariumWorld(roster: Npc[]): { npcs: Npc[]; world: WorldState } {
  const npcs = roster.map((n) => ({ ...n }));
  const base = initWorld(npcs);
  return { npcs, world: { ...base, beds: [], mingle: true } };
}

export async function cliffNoteHistoryEvent(args: {
  worldId: string;
  occurredAt: string;
  a: Npc;
  b: Npc;
  lines: Array<{ speaker: string; text: string }>;
  deviceIds: string[];
}): Promise<HistoryEventRecord | null> {
  if (!deepSeekReady()) return null;
  const responseSchema = z.object({ summary: z.string().max(140) });
  try {
    const response = await chatComplete([
      {
        role: 'system',
        content: 'Return JSON only in the shape {"summary":"..."}. Read the transcript and write one diary sentence about what they talked about: the subject, and what each person said or decided. At most 140 characters. Never mention a line count.',
      },
      {
        role: 'user',
        content: JSON.stringify({
          names: [args.a.firstName, args.b.firstName],
          transcript: args.lines,
        }),
      },
    ], { json: true, maxTokens: 100, temperature: 0.4 });
    const parsed = responseSchema.safeParse(JSON.parse(response));
    const summary = parsed.success ? parsed.data.summary.trim() : '';
    if (!summary || isRetiredHistorySummary(summary)) return null;
  return {
      eventId: uid('evt'),
      worldId: args.worldId,
      occurredAt: args.occurredAt,
      summary,
      actorIds: [args.a.id, args.b.id],
      deviceIds: args.deviceIds,
      observed: true,
    };
  } catch {
    return null;
  }
}

export function appendHistoryEvent(
  state: PersistedRuntimeState,
  event: HistoryEventRecord,
  userPresent = true,
): PersistedRuntimeState {
  return recordHistoryEvent(state, event, userPresent);
}
